import { defineStore } from "pinia";
import { ref } from "vue";

export interface Team {
  id: string;
  name: string;
  logo_url: string;
}

const UNKNOWN_TEAM: Team = {
  id: "unknown",
  name: "Unknown",
  logo_url: "https://www.kindpng.com/picc/m/73-739696_transparent-question-mark-in-circle-hd-png-download.png",
}

export const useTeamStore = defineStore("team", () => {
  const teams = ref<Team[]>([]);
  const loading = ref(false);
  const error = ref<null | string>(null);

  async function fetchAll() {
    loading.value = true;
    error.value = null;
    try {
      const response = await fetch("/api/teams");
      if (!response.ok) {
        throw new Error("Failed to fetch teams");
      }
      teams.value = await response.json() as Team[];
    } catch (err) {
      error.value = "An error occurred while fetching teams";
    } finally {
      loading.value = false;
    }
  }

  async function putTeam(team: Partial<Team>) {
    loading.value = true;
    error.value = null;
    try {
      const response = await fetch("/api/teams", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(team),
      });
      if (!response.ok) {
        throw new Error("Failed to save team");
      }
      await fetchAll(); // Refresh the list after saving
    } catch (err) {
      error.value = "An error occurred while saving the team";
    } finally {
      loading.value = false;
    }
  }

  async function deleteTeam(team: Team) {
    loading.value = true;
    error.value = null;
    try {
      const response = await fetch(`/api/teams/${team.id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        throw new Error("Failed to delete team");
      }
      await fetchAll(); // Refresh the list after deletion
    } catch (err) {
      error.value = "An error occurred while deleting the team";
    } finally {
      loading.value = false;
    }
  }

  function getTeamById(id: string): Team {
    const team = teams.value.find(t => t.id === id);
    return team || UNKNOWN_TEAM;
  }

  // Initial fetch of teams when the store is created
  fetchAll().then();

  return { teams, loading, error, putTeam, deleteTeam, getTeamById, fetchAll };
})
